'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { X, Plus } from 'lucide-react'
import { formatImageURL, formatPrice } from '@/utils/util'
import { useRewardVariantPicker, useVariantSelection } from '@/lib/commerce/hooks'
import VariantOptionPicker from '@/components/blocks/VariantOptionPicker'

function RewardPrice({ price, original }: { price: number, original?: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-[12.5px] font-semibold tabular-nums ${price === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{price === 0 ? 'Үнэгүй' : formatPrice(price)}</span>
      {!!original && price < original && <span className="text-[11.5px] text-foreground/40 line-through tabular-nums">{formatPrice(original)}</span>}
    </div>
  )
}

function RewardOptionChoice({ product, getPrice, onAdd }: { product: any, getPrice: (_variant: any) => number, onAdd: (_variantId: string) => void }) {
  const { selectedVariant, selectedOptions, selectOption, isSelectionPurchasable, isVariantAvailable } = useVariantSelection(product)
  const canAdd = isSelectionPurchasable && !!selectedVariant?.id && isVariantAvailable(selectedVariant)

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-foreground/10">
        <div className="w-14 h-14 shrink-0 rounded-xl overflow-hidden bg-[#F1EEE7]">
          <img src={formatImageURL(selectedVariant?.images?.[0] || product.image || 'placeholder.png')} alt={product.name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0">
          <div className="text-[13.5px] font-semibold text-foreground line-clamp-1">{isSelectionPurchasable ? selectedVariant?.name : 'Энэ хослол байхгүй'}</div>
          {selectedVariant && <div className="mt-0.5"><RewardPrice price={getPrice(selectedVariant)} original={selectedVariant.price} /></div>}
        </div>
      </div>
      <VariantOptionPicker product={product} selected={selectedOptions} onSelect={selectOption} selectedClassName="bg-foreground text-white border-foreground" />
      <button type="button" disabled={!canAdd} onClick={() => { if (selectedVariant?.id) onAdd(selectedVariant.id) }} className="w-full h-11 rounded-full bg-foreground text-cream text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">
        {canAdd ? <><Plus className="size-4" /> Сагсанд нэмэх</> : 'Дууссан байна'}
      </button>
    </div>
  )
}

export default function RewardVariantModal() {
  const { isOpen, close, reward, variants, optionProduct, add, getPrice, isAvailable } = useRewardVariantPicker()

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-60 font-inter" onClose={close}>
        <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </TransitionChild>
        <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
          <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <DialogPanel className="w-full max-w-md max-h-[88vh] bg-cream rounded-3xl shadow-2xl flex flex-col overflow-hidden">
              <header className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/55 mb-1">Бэлгийн бараа</div>
                  <h2 className="text-[22px] font-bold font-gilroy tracking-[-0.015em] text-foreground leading-tight">Төрөл сонгох</h2>
                  <p className="mt-1 text-[12.5px] text-foreground/55 line-clamp-1">{reward?.name}</p>
                </div>
                <button type="button" onClick={close} aria-label="Хаах" className="w-9 h-9 shrink-0 rounded-full bg-foreground/5 hover:bg-foreground/10 inline-flex items-center justify-center text-foreground transition-colors cursor-pointer">
                  <X className="size-4" />
                </button>
              </header>

              <div className="px-6 pb-6 min-h-0 overflow-y-auto">
                {optionProduct ? (
                  <RewardOptionChoice product={optionProduct} getPrice={getPrice} onAdd={add} />
                ) : (
                  <div className="space-y-2">
                    {variants.map((variant: any) => {
                      const available = isAvailable(variant)
                      return (
                        <button key={variant.id} type="button" disabled={!available} onClick={() => add(variant.id)} className="w-full flex items-center gap-3 p-3 bg-white rounded-2xl border border-foreground/10 text-left transition-colors cursor-pointer hover:border-foreground/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-foreground/10">
                          <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-[#F1EEE7]">
                            <img src={formatImageURL(variant.images?.[0] || reward?.image || 'placeholder.png')} alt={variant.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[13.5px] font-semibold text-foreground line-clamp-1">{variant.name}</div>
                            <div className="mt-0.5"><RewardPrice price={getPrice(variant)} original={variant.price} /></div>
                          </div>
                          <span className="h-9 px-3.5 shrink-0 rounded-full bg-foreground text-cream text-[12.5px] font-semibold inline-flex items-center gap-1.5">
                            {available ? <><Plus className="size-3.5" /> Нэмэх</> : 'Дууссан'}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
